import { parsePhoneNumberFromString } from "libphonenumber-js";
import { countryList } from "./CountryList";

// Convert ISO-2 country code (e.g. "US") to the regional-indicator
// emoji flag (e.g. 🇺🇸). Returns null when the input doesn't look like
// a 2-letter code. Note that some platforms (Windows / Chrome on
// Windows) don't render these emojis as flags — prefer <CountryFlag>
// for UI rendering and use this only when an emoji string is wanted.
export function countryCodeToFlag(code) {
  if (!code || typeof code !== "string" || code.length !== 2) return null;
  const cps = code
    .toUpperCase()
    .split("")
    .map((c) => 0x1f1e6 + c.charCodeAt(0) - 65);
  return String.fromCodePoint(...cps);
}

// Resolve a stored dial_code-ish value to an ISO-2 country code.
// Production data is messy: some rows store the numeric dial code
// ("91"), some include a leading plus ("+91"), and some store the
// ISO-2 code directly ("IN"). Handle all three.
//   - "91" / "+91" / 91  → "IN"
//   - "IN" / "in"        → "IN"
//   - anything else      → null
export function isoFromDialCode(dial) {
  if (dial === undefined || dial === null) return null;
  const raw = String(dial).trim();
  if (!raw) return null;

  // Case 1: looks like an ISO-2 code already.
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();

  // Case 2: numeric dial code (with or without leading "+").
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return null;
  let best = null;
  for (const c of countryList) {
    if (c.dial_code === digits) {
      best = c;
      break;
    }
  }
  if (!best) {
    for (const c of countryList) {
      if (
        digits.startsWith(c.dial_code) &&
        (!best || c.dial_code.length > best.dial_code.length)
      ) {
        best = c;
      }
    }
  }
  return best ? best.code : null;
}

// Resolve an E.164 phone number to its ISO-2 country code. Used as a
// fallback when channels.dial_code isn't stored.
export function isoFromPhone(phone) {
  if (!phone) return null;
  try {
    const parsed = parsePhoneNumberFromString(String(phone));
    return parsed?.country || null;
  } catch {
    return null;
  }
}

// Legacy emoji-only helpers — kept for non-UI uses.
export function flagForDialCode(dial) {
  return countryCodeToFlag(isoFromDialCode(dial));
}
export function flagForPhone(phone) {
  return countryCodeToFlag(isoFromPhone(phone));
}
